import { useMemo } from "react";
import { useList } from "@refinedev/core";
import { GRADIENT_KEYS } from "@libertify/ui";
import { useSession } from "hooks/useSession";
import { PlaybookResponse } from "pages/media/types";
import {
  mapPlaybookToDraft,
  mapPlaybookToGeneralSettings,
  playbookHasBrandKit,
} from "../data/addonApi";
import { CarouselPlaybook, PLAYBOOK_PROJECT_TYPE } from "../types";

/**
 * Fetches the current org's playbooks and maps them into the stable
 * `CarouselPlaybook` view-model the carousel renders.
 *
 * This is the ONLY place coupled to the backend playbook shape. When the
 * endpoint/schema changes, update the fetch + mapping here and the rest of the
 * /create-experience flow keeps working unchanged.
 */
export const usePlaybooksForCarousel = (): {
  playbooks: CarouselPlaybook[];
  isLoading: boolean;
  defaultPlaybook: CarouselPlaybook | undefined;
} => {
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;

  const { data, isLoading } = useList<PlaybookResponse>({
    resource: `media/${organizationId}/playbooks`,
    // The endpoint is paginated ({ items, pagination } — unwrapped by the data
    // provider); fetch one large page, the carousel renders them all.
    pagination: { current: 1, pageSize: 50, mode: "server" },
    filters: [{ field: "include_public", operator: "eq", value: true }],
    queryOptions: {
      enabled: !!organizationId,
    },
  });

  const playbooks = useMemo<CarouselPlaybook[]>(
    () =>
      (data?.data ?? []).map((playbook, index) => ({
        id: playbook.id,
        name: playbook.name,
        description: playbook.description ?? undefined,
        gradient: GRADIENT_KEYS[index % GRADIENT_KEYS.length] ?? "default",
        hasBrandKit: playbookHasBrandKit(playbook),
        projectType: PLAYBOOK_PROJECT_TYPE,
        // "General" is the fixed settings card, not an add-on — keep it out
        // of the details modal's add-on list.
        addOns: (playbook.enabled_add_ons?.enabled_add_ons ?? []).filter(
          (type) => type !== "General"
        ),
        addonDraft: mapPlaybookToDraft(playbook),
        generalDraft: mapPlaybookToGeneralSettings(playbook),
        public: playbook.public,
        organizationId: playbook.organization_id,
      })),
    [data?.data]
  );

  const defaultPlaybook = useMemo(() => {
    return playbooks.find((playbook) => playbook.public);
  }, [playbooks]);

  return { playbooks, isLoading, defaultPlaybook };
};
